import React from 'react';
import { Pencil, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Resource } from '../../lib/resources';
import { getTwilioPhoneNumbers, TwilioPhoneNumbers } from '../../lib/resourcePhoneNumber';

// Add system_phone_number to the Resource type through augmentation
declare module '../../lib/resources' {
  interface Resource {
    system_phone_number?: string;
  }
}

interface BasicInformationProps {
  resource: Resource;
  editMode: string | null;
  formData: Partial<Resource>;
  setFormData: (data: Partial<Resource>) => void;
  onSave: (section: string) => void;
  onEdit: (section: string) => void;
  loading?: boolean;
}

interface Role {
  id: string;
  role: string;
}

export default function BasicInformation({
  resource,
  editMode,
  formData,
  setFormData,
  onSave,
  onEdit,
  loading
}: BasicInformationProps) {
  const [roles, setRoles] = React.useState<Role[]>([]);
  const [rolesLoading, setRolesLoading] = React.useState(false);
  const [rolesError, setRolesError] = React.useState<string | null>(null);
  const [twilioPhoneNumbers, setTwilioPhoneNumbers] = React.useState<TwilioPhoneNumbers | null>(null);
  const [phoneNumbersLoading, setPhoneNumbersLoading] = React.useState(false);
  const [clockInUrlEnabled, setClockInUrlEnabled] = React.useState(false);

  const loadRoles = async () => {
    if (!resource.business_id || !supabase) return;
    
    try {
      setRolesLoading(true);
      setRolesError(null);
      
      const { data, error } = await supabase
        .from('resource_permissionsv2')
        .select('id, role')
        .eq('business_id', resource.business_id);

      if (error) throw error;
      setRoles(data || []);
    } catch (err) {
      console.error('Error loading roles:', err);
      setRolesError(err instanceof Error ? err.message : 'Failed to load roles');
    } finally {
      setRolesLoading(false);
    }
  };

  const loadTwilioPhoneNumbers = async () => {
    if (!resource.business_id) return;
    
    try {
      setPhoneNumbersLoading(true);
      const numbers = await getTwilioPhoneNumbers(resource.business_id);
      setTwilioPhoneNumbers(numbers);
      
      // Set initial selected phone number if resource has one
      if (resource.system_phone_number) {
        setFormData({ ...formData, system_phone_number: resource.system_phone_number });
      }
    } catch (err) {
      console.error('Error loading Twilio phone numbers:', err);
    } finally {
      setPhoneNumbersLoading(false);
    }
  };

  const loadClockInUrlSetting = async () => {
    if (!resource.business_id || !supabase) return;
    
    try {
      const { data, error } = await supabase
        .rpc('get_app_setting', {
          p_business_id: resource.business_id,
          p_app_slug: 'payroll',
          p_setting_key: 'clock_in_url_enabled',
          p_default_value: false
        });

      if (!error && data !== null) {
        setClockInUrlEnabled(data);
      }
    } catch (err) {
      console.error('Error loading clock in URL setting:', err);
    }
  };

  React.useEffect(() => {
    loadClockInUrlSetting();
    if (editMode === 'basic') {
      loadRoles();
      loadTwilioPhoneNumbers();
    }
  }, [editMode, resource.business_id]);

  // Update this to actually update the formData
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedNumber = e.target.value;
    // Update the formData so it gets saved when the user clicks "Save"
    setFormData({ ...formData, system_phone_number: selectedNumber });
    console.log('Phone number selected and added to form data:', selectedNumber);
  };

  return (
    <div>
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
        <h3 className="text-base font-semibold text-gray-900">Basic Information</h3>
        {editMode === 'basic' ? (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onEdit('')}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave('basic')}
              disabled={loading}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <span className="flex items-center">
                  <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  Saving...
                </span>
              ) : (
                'Save'
              )}
            </button>
          </div>
        ) : (
          <button
            onClick={() => onEdit('basic')}
            className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Pencil className="h-4 w-4 text-gray-600" />
          </button>
        )}
      </div>
      <div className="p-2 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {editMode === 'basic' ? (
            <>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.first_name || ''}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                  style={{ 
                    minHeight: '44px',
                    fontSize: '16px', // Prevent iOS zoom
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)'
                  }}
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.last_name || ''}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                  style={{ 
                    minHeight: '44px',
                    fontSize: '16px', // Prevent iOS zoom
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)'
                  }}
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                  style={{ 
                    minHeight: '44px',
                    fontSize: '16px', // Prevent iOS zoom
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)'
                  }}
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                  style={{ 
                    minHeight: '44px',
                    fontSize: '16px', // Prevent iOS zoom
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)'
                  }}
                />
              </div>
              
              {/* Clock In Passcode - Only show if clock in URL is enabled */}
              {clockInUrlEnabled && (
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                    Clock In Passcode
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{4}"
                    maxLength={4}
                    value={formData.clock_in_passcode || ''}
                    onChange={(e) => {
                      // Only allow numeric input and limit to 4 digits
                      const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setFormData({ ...formData, clock_in_passcode: value });
                    }}
                    placeholder="0000"
                    className="w-full px-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                    style={{ 
                      minHeight: '44px',
                      fontSize: '16px', // Prevent iOS zoom
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)'
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    4-digit code for clocking in via URL
                  </p>
                </div>
              )}
              
              {/* Not Bookable Toggle */}
              <div className="col-span-1 sm:col-span-2">
                <div className="flex items-center justify-between p-4 bg-orange-50/80 backdrop-blur-sm border border-orange-200/50 rounded-2xl">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900">
                      Not Bookable
                    </label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      When enabled, this team member won't appear when assigning resources to bookings
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, not_bookable: !formData.not_bookable })}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                      formData.not_bookable ? 'bg-orange-500' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        formData.not_bookable ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <div className="text-xs sm:text-sm text-gray-600 font-medium">First Name</div>
                <div className="text-sm sm:text-base text-gray-900 mt-1 font-semibold">{resource.first_name}</div>
              </div>
              <div>
                <div className="text-xs sm:text-sm text-gray-600 font-medium">Last Name</div>
                <div className="text-sm sm:text-base text-gray-900 mt-1 font-semibold">{resource.last_name}</div>
              </div>
              <div>
                <div className="text-xs sm:text-sm text-gray-600 font-medium">Email</div>
                <div className="text-sm sm:text-base text-gray-900 mt-1 font-semibold">{resource.email}</div>
              </div>
              <div>
                <div className="text-xs sm:text-sm text-gray-600 font-medium">Phone</div>
                <div className="text-sm sm:text-base text-gray-900 mt-1 font-semibold">{resource.phone}</div>
              </div>
              
              {/* Clock In Passcode (View Mode) - Only show if clock in URL is enabled */}
              {clockInUrlEnabled && (
                <div>
                  <div className="text-xs sm:text-sm text-gray-600 font-medium">Clock In Passcode</div>
                  <div className="text-sm sm:text-base text-gray-900 mt-1 font-semibold">
                    {resource.clock_in_passcode ? '••••' : 'Not set'}
                  </div>
                </div>
              )}
              
              {/* Not Bookable Status (View Mode) */}
              {resource.not_bookable && (
                <div className="col-span-1 sm:col-span-2">
                  <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-xl">
                    <AlertCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                    <span className="text-sm font-medium text-orange-700">
                      Not Bookable - This team member won't appear when assigning resources to bookings
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}